import { brandInputSchema } from './brand.schema';
import { deriveAccent, type AccentTokens } from './derive';

/** A marca UY3 é ajustada à mão no `lume.css`: sem semente, ou com a semente da UY3, nada é injetado. */
const UY3_SEEDS = new Set(['#d04d27', '#b83d1a']);
const DEFAULT_NAME = 'UYZY';

export interface Brand {
  readonly name: string;
  /** Presente só quando a marca não é a UY3. */
  readonly slug: string | undefined;
  readonly logo: { readonly light: string | undefined; readonly dark: string | undefined };
  readonly mark: { readonly light: string | undefined; readonly dark: string | undefined };
  readonly favicon: string | undefined;
  /**
   * A marca compacta é o "UY3" da casca: tenant raiz (`TENANT_TYPE === 0`) ou corban sem logo nem
   * marca próprios. Corban com marca própria usa a dele.
   */
  readonly uy3: boolean;
  readonly tokens: { readonly light: AccentTokens; readonly dark: AccentTokens } | undefined;
  readonly warnings: readonly string[];
}

const isHex = (v: unknown): v is string => typeof v === 'string' && /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(v);
const norm = (v: string) => (v.length <= 5 ? `#${v.slice(1, 4).replace(/./g, (c) => c + c)}` : v.slice(0, 7)).toLowerCase();

/** Config do corban → marca resolvida. Pura; valor inválido vira aviso, nunca erro de boot (U27). */
export function resolveBrand(config: Record<string, unknown>): Brand {
  const warnings: string[] = [];
  const parsed = brandInputSchema.safeParse(config);
  const input = parsed.success ? parsed.data : {};
  if (!parsed.success) warnings.push(...parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`));
  const block = input.BRAND ?? undefined;
  const palette = config['PALETTE'];
  if (palette && typeof palette === 'object' && 'secunday' in palette) warnings.push('PALETTE.secunday é ignorado');

  const legacyMain = input.PALETTE?.primary?.main ?? undefined;
  const mainColor = isHex(input.MAIN_COLOR) && !['#fff', '#ffffff', '#000', '#000000'].includes(input.MAIN_COLOR.toLowerCase()) ? input.MAIN_COLOR : undefined;
  const seed = block?.accent ?? legacyMain ?? mainColor;
  if (legacyMain && mainColor && norm(legacyMain) !== norm(mainColor)) warnings.push('MAIN_COLOR diverge de PALETTE.primary.main; vale o PALETTE');

  let tokens: Brand['tokens'];
  if (seed && !UY3_SEEDS.has(norm(seed))) {
    const light = deriveAccent(norm(seed), 'light');
    const dark = deriveAccent(norm(block?.accentDark ?? seed), 'dark');
    if (light && dark) {
      tokens = { light, dark };
      if (light.accent !== norm(seed)) warnings.push(`acento ajustado para contraste AA: ${norm(seed)} → ${light.accent}`);
    } else warnings.push(`semente ${seed} não gerou tokens com contraste; vale a marca UY3`);
  }

  const name = block?.name ?? input.MAIN_TITLE ?? DEFAULT_NAME;
  const favicon = block?.favicon ?? input.MAIN_FAVICON ?? undefined;
  const logo = { light: block?.logo?.light ?? input.MAIN_LOGO ?? undefined, dark: block?.logo?.dark ?? input.INVERTED_LOGO ?? undefined };
  const mark = { light: block?.mark?.light ?? favicon, dark: block?.mark?.dark ?? block?.mark?.light ?? favicon };
  const slug = tokens ? name.toLowerCase().normalize('NFD').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'corban' : undefined;
  return {
    name,
    slug,
    logo,
    mark,
    favicon,
    uy3: config['TENANT_TYPE'] === 0 || (!logo.light && !mark.light),
    tokens,
    warnings,
  };
}

const VARS: Record<keyof AccentTokens, string> = {
  accent: '--lume-accent',
  accentHover: '--lume-accent-hover',
  accentPress: '--lume-accent-press',
  accentText: '--lume-accent-text',
  accentWash: '--lume-accent-wash',
  accentLine: '--lume-accent-line',
  fgOnAccent: '--lume-fg-on-accent',
  borderFocus: '--lume-border-focus',
};

/** CSS da marca; especificidade (0,3,0) vence o `lume.css` em qualquer ordem de injeção (discovery/07 §7.6). */
export function brandCss(brand: Brand): string {
  if (!brand.tokens) return '';
  const block = (t: AccentTokens) => (Object.keys(VARS) as (keyof AccentTokens)[]).map((k) => `${VARS[k]}:${t[k]}`).join(';');
  return `:root[data-brand][data-theme='light']{${block(brand.tokens.light)}}:root[data-brand][data-theme='dark']{${block(brand.tokens.dark)}}`;
}
